import React, { Component } from 'react';
import { Query } from 'react-apollo';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import DataUpdater from './DataUpdater';
import withContext from '../withContext';

/**
 * HOC that  will allow us to save in the context not only the data
 * return by the Query but also all the metadata passed to child
 * function of the Query component.
 * 
 * Paramenters:
 * 
 * contexConsumer: Context.Consumer
 *  Consumer on the context we want are Query to be connected to

 * dataName?: string | Array<string>
 * The key, or keys, the result of the query will be stored in the context. Defaults to ‘data’

 * options?: Object
 * Object representing options to customize our resulting component. Currently it accepts:
 */

const contextedQuery = (contextConsumer, dataName = 'data', options = {}) => {
	/**
	 * PureComponent isn't used as recommended here: https://bitbucket.org/lavu/controlpanel/wiki/Context%20(Usage%20of%20React%20Context)
	 * because we implement our own validation to check if the component should
	 * update or not
	 */

	class PureContextQuery extends Component {
		state = {};

		static propTypes = {
			/**
			 * updateContext: Function that updates the context, coming from the
			 * context itself using options.contextUpdater
			 */
			updateContext: PropTypes.func.isRequired,
			variables: PropTypes.object,
		};

		static defaultProps = { variables: {} };

		/**
		 * PureComponent isn't used as it's needed to check also for deep equality in
		 * the variables object to prevent an infinite loop of re-renders. Because of
		 * this, a shallow comparison is performed for all props except `variables`.
		 *
		 * The state is omitted as it's not used in this component
		 */
		shouldComponentUpdate(nextProps) {
			const { variables, ...shallowComparisonProps } = nextProps;

			return !(
				Object.keys(shallowComparisonProps).every(
					key => shallowComparisonProps[key] === this.props[key]
				) && isEqual(variables, this.props.variables)
			);
		}

		render() {
			const { updateContext, ...queryProps } = this.props;

			return (
				<Query {...queryProps}>
					{queryResult => (
						<DataUpdater
							updater={updateContext}
							dataName={dataName}
							{...queryResult}
						/>
					)}
				</Query>
			);
		}
	}

	return withContext(contextConsumer)(
		contextValue => ({
			updateContext: contextValue[options.contextUpdater || 'updateContext'],
		}),
		{
			displayName: options.displayName || 'ContextQuery',
		}
	)(PureContextQuery);
};

export default contextedQuery;
